import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse } from '../types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Callers show `error.message`; make it the server's explanation (e.g.
    // "This month is closed") rather than Axios's "Request failed with status code 409".
    const serverMessage = error.response?.data?.message;
    if (typeof serverMessage === 'string' && serverMessage) error.message = serverMessage;

    // Handle 401 errors by redirecting to login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * What callers catch from apiService: the server's JSON body with the HTTP
 * status added. A body that isn't JSON (an HTML error page from a proxy or
 * an unknown route) becomes a plain error carrying the status, so callers
 * always get a readable `message`.
 */
const toApiError = (error: any): any => {
  const data = error.response?.data;
  const apiError: any = data && typeof data === 'object'
    ? data
    : { success: false, message: error.response ? `Request failed (${error.response.status})` : 'Network error' };
  apiError.status = error.response?.status;
  return apiError;
};

// Generic API service methods
export const apiService = {
  // GET request
  get: async <T>(url: string, params?: any): Promise<ApiResponse<T>> => {
    try {
      const response = await api.get(url, { params });
      return response.data;
    } catch (error: any) {
      // Preserve status code in the error object
      throw toApiError(error);
    }
  },

  // POST request
  post: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post(url, data);
      return response.data;
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // PUT request
  put: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response = await api.put(url, data);
      return response.data;
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // PATCH request
  patch: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response = await api.patch(url, data);
      return response.data;
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // DELETE request
  delete: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response = await api.delete(url);
      return response.data;
    } catch (error: any) {
      throw toApiError(error);
    }
  },

  // File upload
  upload: async <T>(url: string, formData: FormData): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Network error' };
    }
  },

  // Download file
  download: async (url: string, filename: string): Promise<void> => {
    try {
      const response = await api.get(url, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(link.href);
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Download failed' };
    }
  },

  // POST request for binary data (PDFs, etc.)
  postBinary: async (url: string, data?: any): Promise<Blob> => {
    try {
      const response = await api.post(url, data, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Network error' };
    }
  },
};

export default api; 